import React, { useState } from 'react';
import { ThemeLoader, StyleLoader } from '@sightworks/theme';
import Icon from '@material-ui/core/Icon';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import FacebookIcon from '@material-ui/icons/Facebook';
import InstagramIcon from '@material-ui/icons/Instagram';
import TwitterIcon from '@material-ui/icons/Twitter';
import YouTubeIcon from '@material-ui/icons/YouTube';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import { createSvgIcon } from '@material-ui/core';

import IconProps from './props';
import { Theme } from '@material-ui/core';

const SlideShareIcon = createSvgIcon(
	<path d="M 4.8 1.44 C 3.48 1.44 2.4 2.52 2.4 3.84 L 2.4 10.41 C 2.175 10.198125119999998 1.9893748799999997 10.01625024 1.8956251199999998 9.9225 L 1.84125024 9.86625024 C 1.7531251199999998 9.78375024 1.57875024 9.73875024 1.4681251199999998 9.73875024 C 1.13625024 9.73875024 0.96 9.90937488 0.96 10.08 C 0.9618748800000001 10.47562512 1.1099999999999999 10.781250239999999 1.14375024 10.86 C 1.635 12.05812512 3.8175 14.37937488 5.475 15 C 4.9725 16.005 4.9725 18.51375024 4.9725 18.51375024 C 4.9725 20.66625024 6.58875024 22.455 8.7975 22.529999999999998 L 9.51562512 22.529999999999998 C 10.62 22.455 11.50312512 21.57187488 11.497499999999999 20.467499999999998 L 11.497499999999999 16.26937488 C 11.7 16.35562512 12.06937488 16.44375024 12.5025 16.479374879999998 L 12.5025 20.5425 C 12.5025 21.63937488 13.393125119999999 22.529999999999998 14.49 22.529999999999998 L 15.25125024 22.529999999999998 C 17.43937488 22.529999999999998 19.0275 20.75812512 19.0275 18.51375024 C 19.0275 18.51375024 19.0275 16.005 18.525 15 C 20.0475 14.362499999999999 22.14 12.25687488 22.83 10.987499999999999 C 22.873125119999997 10.90875024 23.04 10.47375024 23.04 9.98437488 C 23.04 9.86625024 22.93312512 9.58875024 22.59937488 9.58875024 C 22.36687488 9.58875024 22.2075 9.795 22.2075 9.795 C 22.098750239999998 9.903750239999999 21.88125024 10.125 21.599999999999998 10.385625119999998 L 21.599999999999998 3.84 C 21.599999999999998 2.52 20.52 1.44 19.2 1.44 Z M 4.8 2.4 L 19.2 2.4 C 20.00062512 2.4 20.64 3.0393748799999996 20.64 3.84 L 20.64 11.19 C 19.50187488 12.06187488 17.87437488 12.99187488 16.01437488 12.99187488 L 13.792499999999999 12.99187488 C 13.063125119999999 12.99187488 12.5025 13.61062512 12.5025 14.283750239999998 L 12.5025 14.67937488 C 12.059999999999999 14.43562512 11.55937488 14.013750239999998 10.995 13.34437488 C 10.995 13.34437488 10.775625119999999 12.99187488 10.1775 12.99187488 L 7.985625119999999 12.99187488 C 6.09 12.99187488 4.47 12.06937488 3.36 11.22187488 L 3.36 3.84 C 3.36 3.0393748799999996 3.99937488 2.4 4.8 2.4 Z M 8.4 6.72 C 6.94125024 6.72 5.76 7.9012502399999995 5.76 9.36 C 5.76 10.818750239999998 6.94125024 12 8.4 12 C 9.858750239999999 12 11.04 10.818750239999998 11.04 9.36 C 11.04 7.9012502399999995 9.858750239999999 6.72 8.4 6.72 Z M 15.6 6.72 C 14.14125024 6.72 12.959999999999999 7.9012502399999995 12.959999999999999 9.36 C 12.959999999999999 10.818750239999998 14.14125024 12 15.6 12 C 17.05875024 12 18.24 10.818750239999998 18.24 9.36 C 18.24 7.9012502399999995 17.05875024 6.72 15.6 6.72 Z"/>,
	'SlideShare'
);

const IconBlock = ({ classes, icon, color }: IconProps, ref: React.Ref<any>) => {
	const iconComponents = {
		facebook: FacebookIcon,
		instagram: InstagramIcon,
		twitter: TwitterIcon,
		youtube: YouTubeIcon,
		linkedin: LinkedInIcon,
		slideshare: SlideShareIcon,
		default: Icon,
	};

	if (!icon) return null;
	
	const Component: React.FC<{className: string, ref: React.Ref<any>}> = (() => {
		let name: keyof (typeof iconComponents);
		let v: string = icon.split('svg:')[1] || 'default';
		if (v in iconComponents) name = v as typeof name;
		else name = 'default';
		return name in iconComponents ? iconComponents[name] : iconComponents.default;
	})();

	const IconBlock = (
		<Component
			className={clsx(classes.root, {
				[classes.default]: color === 'default',
				[classes.primary]: color === 'primary',
				[classes.secondary]: color === 'secondary',
			})}
			ref={ref}
		>
			{icon.startsWith('svg:') ? null : icon}
		</Component>
	);

	return IconBlock;
};

export default ThemeLoader(StyleLoader(
	IconBlock,
	makeStyles(
		(theme: Theme) => ({
			root: {},
			default: {
				color: theme.palette.text.primary,
			},
			primary: {
				color: theme.palette.primary.main,
			},
			secondary: {
				color: theme.palette.secondary.main,
			},
		}),
		{ name: 'SWIcon' }
	)
));
